import React from "react";
import * as SubframeCore from "@subframe/core";

interface MetricCardProps {
  title: string;
  subtitle: string;
  value: string | number;
  unit?: string;
  isLowerBetter?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, unit, isLowerBetter }) => (
  <div className="flex w-full sm:w-44 flex-none flex-col items-start gap-2 rounded-lg border border-solid border-neutral-border bg-default-background px-3 sm:px-4 py-3 sm:py-4 shadow-sm">
    <div className="flex w-full flex-col items-center gap-2">
      <div className="flex flex-col items-center">
        <span className="text-sm sm:text-body font-body text-default-font text-center">
          {title}
        </span>
        <span className="text-label font-label text-subtext-color">
          ({subtitle})
        </span>
        {isLowerBetter && (
          <span className="text-xs sm:text-label font-label text-subtext-color">
            (Lower is better)
          </span>
        )}
      </div>
      <span className="text-lg sm:text-header font-header text-neutral-900">
        {value}{unit}
      </span>
    </div>
  </div>
);

interface MetricBarProps {
  label: string;
  value: string | number;
  percentage: number;
  color: string;
  isBold?: boolean;
}

const MetricBar: React.FC<MetricBarProps> = ({ label, value, percentage, color, isBold = false }) => (
  <div className="flex w-full items-center gap-1">
    <span className={`w-8 sm:w-10 flex-none ${isBold ? 'text-sm sm:text-body-bold font-body-bold text-default-font' : 'text-xs sm:text-label font-label text-subtext-color'}`}>
      {label}
    </span>
    <span className={`w-8 sm:w-10 flex-none ${isBold ? 'text-xs sm:text-label font-label text-default-font' : 'text-xs sm:text-label font-label text-subtext-color'}`}>
      {value}
    </span>
    <div className="flex h-2 grow shrink-0 basis-0 items-center rounded-sm bg-gradient-to-r from-success-100 via-warning-100 to-error-100">
      <div className="flex items-start self-stretch rounded-l-full" style={{ backgroundColor: color, width: `${percentage}%` }} />
    </div>
  </div>
);

interface MetricSectionProps {
  title: string;
  subtitle: string;
  avgValue: string | number;
  avgUnit?: string;
  yourValue: string | number;
  bestValue: string | number;
  avgPercentage: number;
  yourPercentage: number;
  bestPercentage: number;
  isLowerBetter?: boolean;
  tip?: string;
}

const MetricSection: React.FC<MetricSectionProps> = ({
  title,
  subtitle,
  avgValue,
  avgUnit,
  yourValue,
  bestValue,
  avgPercentage,
  yourPercentage,
  bestPercentage,
  isLowerBetter = false,
  tip
}) => (
  <div className="flex flex-col items-start gap-2 w-full">
    <span className="text-base sm:text-subheader font-subheader text-default-font">
      {title}
    </span>

    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 w-full">
      <MetricCard 
        title={`Avg. ${title}`} 
        subtitle={subtitle}
        value={avgValue} 
        unit={avgUnit} 
        isLowerBetter={isLowerBetter}
      />
      <div className="flex flex-col items-start gap-2 w-full sm:w-auto">
        <div className="flex w-full sm:w-112 flex-col items-start gap-2">
          <div className="flex w-full sm:w-112 flex-col items-start gap-1">
            <MetricBar
              label="Best"
              value={bestValue}
              percentage={bestPercentage}
              color="#99e0ffff"
            />
            <MetricBar
              label="You"
              value={yourValue}
              percentage={yourPercentage}
              color="#60a5fa"
              isBold={true}
            />
            <MetricBar
              label="Avg."
              value={avgValue}
              percentage={avgPercentage}
              color="#fdd091ff"
            />
          </div>
        </div>
        {tip && (
          <div className="flex items-center gap-2">
            <SubframeCore.Icon
              className="text-sm sm:text-body font-body text-brand-primary"
              name="FeatherRocket"
            />
            <span className="text-xs sm:text-label font-label text-brand-primary">
              {tip}
            </span>
          </div>
        )}
      </div>
    </div>
  </div>
);

interface PerformanceMetricsProps {
  responseRate: {
    avg: number;
    yourValue: number;
    bestValue: number;
    avgPercentage: number;
    yourPercentage: number;
    bestPercentage: number;
  };
  sentimentScore: {
    avg: number;
    yourValue: number;
    bestValue: number;
    avgPercentage: number;
    yourPercentage: number;
    bestPercentage: number;
  };
  timeOfResolve: {
    avg: number;
    yourValue: number;
    bestValue: number;
    avgPercentage: number;
    yourPercentage: number;
    bestPercentage: number;
  };
}

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
  responseRate,
}) => {
  return (
    <SubframeCore.Tooltip.Provider>
      <SubframeCore.Tooltip.Root>
        <SubframeCore.Tooltip.Trigger asChild={true}>
          <div className="flex w-full max-w-[1024px] flex-col items-start gap-4 px-4 sm:px-0 opacity-40 cursor-not-allowed">
            <span className="w-full text-lg sm:text-section-header font-section-header text-default-font">
              Your key performance
            </span>
            <div className="flex w-full flex-col items-center rounded-lg bg-default-background shadow-sm pointer-events-none">
              <div className="flex w-full flex-col items-start gap-4 sm:gap-6 rounded-lg bg-default-background px-4 sm:px-6 py-4 sm:py-6 shadow-sm">
                <div className="flex w-full flex-col items-start gap-6 sm:gap-8">
                  <MetricSection
                    title="Time to Resolve"
                    subtitle="Lower is better"
                    avgValue="X"
                    avgUnit=" days"
                    yourValue="X"
                    bestValue="X"
                    avgPercentage={50}
                    yourPercentage={75}
                    bestPercentage={25}
                    tip="Respond within 48 hours to improve your performance by 30%"
                  />
                </div>
              </div>
            </div>
          </div>
        </SubframeCore.Tooltip.Trigger>
        <SubframeCore.Tooltip.Portal>
          <SubframeCore.Tooltip.Content
            className="max-w-xs"
            side="top"
            align="center"
            sideOffset={8}
          >
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-neutral-900 text-white text-sm shadow-lg">
              <SubframeCore.Icon
                className="text-sm"
                name="FeatherClock"
              />
              <span>This feature is coming soon!</span>
            </div>
          </SubframeCore.Tooltip.Content>
        </SubframeCore.Tooltip.Portal>
      </SubframeCore.Tooltip.Root>
    </SubframeCore.Tooltip.Provider>
  );
}; 